import {
  ActiveLocationsModal,
  AdminSpinner,
  AllProvidersModal,
  AreaTooltip,
  ChartDataField,
  ComparedDeliveriesOverviewEntity,
  ComparedPeriodType,
  DeliveriesOverviewEntity,
  Div,
  DragScrollX,
  GroupByType,
  NumberType,
  P,
  ReusableChart,
  calculatePercentageChange,
  formatAmount,
  formatDateByGroupByTypeChart,
  lightTheme,
  renderPercentageChange,
} from '@vgrubs/components';
import { Dispatch, SetStateAction } from 'react';

//TODO MAKE REUSABLE

const CustomXAxisTick = ({
  x,
  y,
  payload,
  groupByType,
}: {
  x: number;
  y: number;
  payload: { value: string };
  groupByType: GroupByType;
}) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={14}
        textAnchor="middle"
        fontSize="12px"
        fill={lightTheme.lightGray}
      >
        {formatDateByGroupByTypeChart(payload.value, groupByType)}
      </text>
    </g>
  );
};

//TODO MAKE REUSABLE

// Custom tooltip components
const ChartTooltip = ({
  groupByType,
  currency,
  label,
  payload,
  textColor,
  isInverted,
}: {
  groupByType: GroupByType;
  currency: NumberType;
  label: string;
  payload: any;
  textColor?: string;
  isInverted?: boolean;
}) => {
  const firstDataKey = payload?.[0]?.dataKey.split('.')[0];
  const secondDataKey = payload?.[1]?.dataKey.split('.')[0];

  const firstDate = payload?.[0]?.payload?.[firstDataKey]?.key;
  const secondDate = payload?.[1]?.payload?.[secondDataKey]?.key;

  const firstValue = payload?.[0]?.value;
  const secondValue = payload?.[1]?.value;

  const calculatedPercentage = calculatePercentageChange(
    firstValue as number,
    secondValue as number
  );

  return (
    <AreaTooltip>
      <Div $flex $center $gap="8px">
        <div>
          {firstDate && (
            <P $m="0">
              {formatDateByGroupByTypeChart(firstDate, groupByType, true)}
              {': '}
              <span style={{ color: textColor }}>
                {formatAmount(firstValue as number, currency)}
              </span>
            </P>
          )}
          {secondDate && (
            <P $m="0">
              {formatDateByGroupByTypeChart(secondDate, groupByType, true)}
              {': '}
              <span style={{ color: lightTheme.darkGray }}>
                {formatAmount(secondValue as number, currency)}
              </span>
            </P>
          )}
        </div>
        {firstDate &&
          secondDate &&
          renderPercentageChange(calculatedPercentage, isInverted || false)}
      </Div>
    </AreaTooltip>
  );
};

//TODO MAKE REUSABLE

const CurrencyYAxisTick = ({ x, y, payload, currency }: any) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={-5}
      y={-13}
      dy={16}
      fontSize="14px"
      textAnchor="end"
      fill={lightTheme.lightGray}
    >
      {formatAmount(payload.value, currency, true)}
    </text>
  </g>
);

// Helper function to create chart fields
const createChartField = (
  dataKey: string,
  color: string,
  options: Partial<ChartDataField> = {}
): ChartDataField => ({
  dataKey,
  color,
  strokeWidth: 2,
  fillOpacity: 1,
  strokeOpacity: 1,
  ...options,
});

export const DeliveriesCharts = ({
  data,
  comparedData,
  loading,
  setAllProvidersModal,
  groupByType = GroupByType.DAY,
  periodTypeComparedTo,
  customComponent,
}: {
  setAllProvidersModal?: Dispatch<SetStateAction<AllProvidersModal | null>>;
  data?: DeliveriesOverviewEntity | null;
  comparedData?: ComparedDeliveriesOverviewEntity | null;
  loading?: boolean;
  groupByType?: GroupByType;
  periodTypeComparedTo?: ComparedPeriodType;
  customComponent?: React.ReactNode;
}) => {
  const isComparing =
    periodTypeComparedTo === ComparedPeriodType.LAST_MONTH ||
    periodTypeComparedTo === ComparedPeriodType.LAST_YEAR ||
    periodTypeComparedTo === ComparedPeriodType.CUSTOM;

  const compareToTitle = (title: string) => {
    if (periodTypeComparedTo === ComparedPeriodType.LAST_MONTH) {
      return `${title} Last Month`;
    } else if (periodTypeComparedTo === ComparedPeriodType.LAST_YEAR) {
      return `${title} Last Year`;
    } else if (periodTypeComparedTo === ComparedPeriodType.CUSTOM) {
      return `${title} Custom Date Range`;
    }
    return '';
  };

  return (
    <Div $relative>
      {loading && (
        <AdminSpinner small absolute overflowBorderRadius="20px" creme />
      )}
      <DragScrollX>
        {/* Total Sales Chart */}

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total Sales"
          titleCompare={compareToTitle('Sales')}
          currency={NumberType.USD}
          totalValue={
            isComparing
              ? comparedData?.totalSalesSum || 0
              : data?.totalSalesSum || 0
          }
          totalValueToCompare={comparedData?.totalSalesSumToCompare || 0}
          valueColor={lightTheme.darkBlue}
          data={data?.totalSales || []}
          dataToCompare={comparedData?.comparedTotalSales}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Total Sales'}
          chartFields={[
            createChartField('totalSales', lightTheme.darkBlue, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField('totalSales.totalSales', lightTheme.darkBlue, {
              stackId: '2',
            }),
            createChartField(
              'totalSalesToCompare.totalSales',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.totalSalesByProvider &&
            data.totalSalesByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalSalesSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalSalesByProvider &&
            comparedData.comparedTotalSalesByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalSalesSum,
                totalValueToCompare: item.totalSalesSumToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        {/* Active Locations Chart - location list on arrow, no per-provider breakdown */}

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Active Locations"
          titleCompare={compareToTitle('Active Locations')}
          currency={NumberType.NUMBER}
          totalValue={
            isComparing
              ? comparedData?.activeStoresCount || 0
              : data?.activeStoresCount || 0
          }
          totalValueToCompare={comparedData?.activeStoresCountToCompare || 0}
          valueColor={lightTheme.blue}
          data={data?.activeStores || []}
          dataToCompare={comparedData?.comparedActiveStores}
          groupByType={groupByType}
          chartFields={[
            createChartField('activeStores', lightTheme.blue, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField('activeStores.activeStores', lightTheme.blue, {
              stackId: '2',
            }),
            createChartField(
              'activeStoresToCompare.activeStores',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          customArrow={
            <ActiveLocationsModal
              title="Active Locations"
              isComparing={isComparing}
              list={
                isComparing
                  ? comparedData?.activeStoresList
                  : data?.activeStoresList
              }
              listToCompare={comparedData?.activeStoresListToCompare}
            />
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total Completed"
          titleCompare={compareToTitle('Completed')}
          currency={NumberType.NUMBER}
          totalValue={
            isComparing
              ? comparedData?.totalCompletedCount || 0
              : data?.totalCompletedCount || 0
          }
          totalValueToCompare={comparedData?.totalCompletedCountToCompare || 0}
          valueColor={lightTheme.green}
          data={data?.totalCompleted || []}
          dataToCompare={comparedData?.comparedTotalCompleted}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Total Completed'}
          chartFields={[
            createChartField('totalCompleted', lightTheme.green, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField(
              'totalCompleted.totalCompleted',
              lightTheme.green,
              {
                stackId: '2',
              }
            ),
            createChartField(
              'totalCompletedToCompare.totalCompleted',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.totalCompletedByProvider &&
            data.totalCompletedByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalCompletedCount,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalCompletedByProvider &&
            comparedData.comparedTotalCompletedByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalCompletedCount,
                totalValueToCompare: item.totalCompletedCountToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total Failed"
          titleCompare={compareToTitle('Failed')}
          currency={NumberType.NUMBER}
          totalValue={
            isComparing
              ? comparedData?.totalCancelledCount || 0
              : data?.totalCancelledCount || 0
          }
          totalValueToCompare={comparedData?.totalCancelledCountToCompare || 0}
          valueColor={lightTheme.primary}
          data={data?.totalCancelled || []}
          dataToCompare={comparedData?.comparedTotalCancelled}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Total Failed'}
          chartFields={[
            createChartField('totalCancelled', lightTheme.primary, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField(
              'totalCancelled.totalCancelled',
              lightTheme.primary,
              {
                stackId: '2',
              }
            ),
            createChartField(
              'totalCancelledToCompare.totalCancelled',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.totalCancelledByProvider &&
            data.totalCancelledByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalCancelledCount,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalCancelledByProvider &&
            comparedData.comparedTotalCancelledByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalCancelledCount,
                totalValueToCompare: item.totalCancelledCountToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
          customComponent={customComponent}
        />

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Average Delivery Time"
          titleCompare={compareToTitle('Average Delivery Time')}
          currency={NumberType.MINUTES}
          totalValue={
            isComparing
              ? comparedData?.averageDeliveryTimeAverage || 0
              : data?.averageDeliveryTimeAverage || 0
          }
          totalValueToCompare={
            comparedData?.averageDeliveryTimeAverageToCompare || 0
          }
          valueColor={lightTheme.orange}
          data={data?.averageDeliveryTime || []}
          dataToCompare={comparedData?.comparedAverageDeliveryTime}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Average Delivery Time'}
          chartFields={[
            createChartField('averageDeliveryTime', lightTheme.orange, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField(
              'averageDeliveryTime.averageDeliveryTime',
              lightTheme.orange,
              {
                stackId: '2',
              }
            ),
            createChartField(
              'averageDeliveryTimeToCompare.averageDeliveryTime',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.averageDeliveryTimeByProvider &&
            data.averageDeliveryTimeByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.averageDeliveryTimeSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedAverageDeliveryTimeByProvider &&
            comparedData.comparedAverageDeliveryTimeByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.averageDeliveryTimeSum,
                totalValueToCompare: item.averageDeliveryTimeSumToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Average Assignment Time"
          titleCompare={compareToTitle('Average Assignment Time')}
          currency={NumberType.MINUTES}
          totalValue={
            isComparing
              ? comparedData?.averageAssignmentTimeAverage || 0
              : data?.averageAssignmentTimeAverage || 0
          }
          totalValueToCompare={
            comparedData?.averageAssignmentTimeAverageToCompare || 0
          }
          valueColor={lightTheme.orange}
          data={data?.averageAssignmentTime || []}
          dataToCompare={comparedData?.comparedAverageAssignmentTime}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Average Assignment Time'}
          chartFields={[
            createChartField('averageAssignmentTime', lightTheme.orange, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField(
              'averageAssignmentTime.averageAssignmentTime',
              lightTheme.orange,
              {
                stackId: '2',
              }
            ),
            createChartField(
              'averageAssignmentTimeToCompare.averageAssignmentTime',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.averageAssignmentTimeByProvider &&
            data.averageAssignmentTimeByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.averageAssignmentTimeSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedAverageAssignmentTimeByProvider &&
            comparedData.comparedAverageAssignmentTimeByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.averageAssignmentTimeSum,
                totalValueToCompare: item.averageAssignmentTimeSumToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total DSP Fee"
          titleCompare={compareToTitle('Total DSP Fee')}
          currency={NumberType.USD}
          totalValue={
            isComparing
              ? comparedData?.totalDSPFeesSum || 0
              : data?.totalDSPFeesSum || 0
          }
          totalValueToCompare={comparedData?.totalDSPFeesSumToCompare || 0}
          valueColor={lightTheme.darkBlue}
          data={data?.totalDSPFees || []}
          dataToCompare={comparedData?.comparedTotalDSPFees}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Total DSP Fee'}
          chartFields={[
            createChartField('totalDSPFees', lightTheme.darkBlue, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField('totalDSPFees.totalDSPFees', lightTheme.darkBlue, {
              stackId: '2',
            }),
            createChartField(
              'totalDSPFeesToCompare.totalDSPFees',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.totalDSPFeesByProvider &&
            data.totalDSPFeesByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalDSPFeesSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalDSPFeesByProvider &&
            comparedData.comparedTotalDSPFeesByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalDSPFeesSum,
                totalValueToCompare: item.totalDSPFeesSumToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total Tip"
          titleCompare={compareToTitle('Total Tip')}
          currency={NumberType.USD}
          totalValue={
            isComparing
              ? comparedData?.totalTipsSum || 0
              : data?.totalTipsSum || 0
          }
          totalValueToCompare={comparedData?.totalTipsSumToCompare || 0}
          valueColor={lightTheme.darkBlue}
          data={data?.totalTips || []}
          dataToCompare={comparedData?.comparedTotalTips}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Total Tip'}
          chartFields={[
            createChartField('totalTips', lightTheme.darkBlue, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField('totalTips.totalTips', lightTheme.darkBlue, {
              stackId: '2',
            }),
            createChartField(
              'totalTipsToCompare.totalTips',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.totalTipsByProvider &&
            data.totalTipsByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalTipsSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalTipsByProvider &&
            comparedData.comparedTotalTipsByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalTipsSum,
                totalValueToCompare: item.totalTipsSumToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Average Tip"
          titleCompare={compareToTitle('Average Tip')}
          currency={NumberType.USD}
          totalValue={
            isComparing
              ? comparedData?.averageTipsAverage || 0
              : data?.averageTipsAverage || 0
          }
          totalValueToCompare={comparedData?.averageTipsAverageToCompare || 0}
          valueColor={lightTheme.darkBlue}
          data={data?.averageTips || []}
          dataToCompare={comparedData?.comparedAverageTips}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Average Tip'}
          chartFields={[
            createChartField('averageTips', lightTheme.darkBlue, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField('averageTips.averageTips', lightTheme.darkBlue, {
              stackId: '2',
            }),
            createChartField(
              'averageTipsToCompare.averageTips',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.averageTipsByProvider &&
            data.averageTipsByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.averageTipsSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedAverageTipsByProvider &&
            comparedData.comparedAverageTipsByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.averageTipsSum,
                totalValueToCompare: item.averageTipsSumToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />
      </DragScrollX>
    </Div>
  );
};
